import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type {
    EmailTemplate as EmailTemplateT,
    LifecycleCampaign,
    LifecycleCampaignType,
} from '@/types/communications'
import { TemplatePicker } from './TemplatePicker'
import { SlotValueForm } from './SlotValueForm'
import { PreviewPane } from './PreviewPane'
import {
    Badge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Field,
    Input,
    LoadingPanel,
    PillButton,
    SectionTitle,
    Toggle,
} from './_shared'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import {
    Sparkles,
    CalendarClock,
    CalendarDays,
    TrendingDown,
    Save,
    ChevronLeft,
} from 'lucide-react'

// Use user.*-prefixed keys so the "Insert variable" menu produces markers
// the renderer auto-binds per-recipient at send time.
const CAMPAIGN_VARIABLES = [
    { key: 'user.firstName', label: 'First name' },
    { key: 'user.name', label: 'Full name' },
    { key: 'user.email', label: 'Email' },
    { key: 'user.orgName', label: 'Organization name' },
]

const CAMPAIGN_META: Record<
    LifecycleCampaignType,
    {
        label: string
        eyebrow: string
        icon: React.ComponentType<{ className?: string }>
        description: string
        defaultSubject: string
        defaultBody: string
    }
> = {
    WELCOME: {
        label: 'Welcome series',
        eyebrow: 'Day 0 → Day 3',
        icon: Sparkles,
        description:
            'Greets new users immediately after signup. Sets expectations and points to first feature to try.',
        defaultSubject: 'Welcome to Libra, {{firstName}}',
        defaultBody:
            '<p>Hey {{firstName}},</p><p>Glad you’re here. Connect your first source from the dashboard to start getting answers across your work.</p><p>— Team Libra</p>',
    },
    DORMANT_7: {
        label: 'Day 7 re-engagement',
        eyebrow: 'Inactive 7 days',
        icon: CalendarClock,
        description:
            "First nudge for users who haven't opened the app in a week. Calls back to a feature they previously used.",
        defaultSubject: "Haven't seen you in a while",
        defaultBody:
            '<p>Hi {{firstName}},</p><p>It’s been a quiet week on Libra. Pick up where you left off whenever you’re ready.</p>',
    },
    DORMANT_14: {
        label: 'Day 14 re-engagement',
        eyebrow: 'Inactive 14 days',
        icon: CalendarDays,
        description:
            'Stronger pull for users still inactive after two weeks. Often a last-chance check-in before churn.',
        defaultSubject: 'We miss you at Libra',
        defaultBody:
            '<p>Hi {{firstName}},</p><p>Anything we can help with? Reply to this email and we’ll dig in.</p>',
    },
    FEATURE_ADOPTION: {
        label: 'Feature adoption',
        eyebrow: 'Used X but not Y',
        icon: TrendingDown,
        description:
            "Surfaces underused features to users who've engaged elsewhere. Use sparingly — runs daily on a rolling cohort.",
        defaultSubject: 'A Libra feature you might have missed',
        defaultBody:
            '<p>Hi {{firstName}},</p><p>Quick tip: try connecting your calendar to get auto-prepped meeting briefs.</p>',
    },
}

export function CampaignsTab() {
    const [campaigns, setCampaigns] = useState<LifecycleCampaign[]>([])
    const [loading, setLoading] = useState(true)
    const [active, setActive] = useState<LifecycleCampaignType | null>(null)

    const load = async () => {
        setLoading(true)
        try {
            const res = await adminApi.getCampaigns()
            setCampaigns(res.items)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const activeCampaign = active ? campaigns.find(c => c.type === active) : null

    if (active && activeCampaign) {
        return (
            <CampaignEditor
                campaign={activeCampaign}
                onBack={() => setActive(null)}
                onSaved={async () => {
                    await load()
                }}
            />
        )
    }

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Lifecycle campaigns"
                description="Automated sends triggered by user state — signup, inactivity, feature adoption gaps. Configure copy and cadence per campaign."
            />

            {loading ? (
                <LoadingPanel label="Loading campaigns…" />
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {campaigns.map(c => {
                        const meta = CAMPAIGN_META[c.type]
                        if (!meta) return null
                        const Icon = meta.icon
                        return (
                            <button
                                key={c.type}
                                onClick={() => setActive(c.type)}
                                className="group rounded-lg border border-gray-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md"
                            >
                                <div className="mb-3 flex items-start justify-between">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <Badge variant={c.enabled ? 'success' : 'default'}>
                                        {c.enabled ? 'on' : 'off'}
                                    </Badge>
                                </div>
                                <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-700">
                                    {meta.label}
                                </h3>
                                <p className="mt-0.5 text-xs uppercase tracking-wider text-gray-500">
                                    {meta.eyebrow}
                                </p>
                                <p className="mt-2 line-clamp-2 text-sm text-gray-600">
                                    {meta.description}
                                </p>
                                {c.subject && (
                                    <div className="mt-3 border-t border-gray-100 pt-3">
                                        <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                            Current subject
                                        </p>
                                        <p className="mt-0.5 truncate text-xs text-gray-900">
                                            {c.subject}
                                        </p>
                                    </div>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

function CampaignEditor({
    campaign,
    onBack,
    onSaved,
}: {
    campaign: LifecycleCampaign
    onBack: () => void
    onSaved: () => void | Promise<void>
}) {
    const meta = CAMPAIGN_META[campaign.type]
    const [enabled, setEnabled] = useState(campaign.enabled)
    const [subject, setSubject] = useState(campaign.subject ?? meta.defaultSubject)
    const [bodyHtml, setBodyHtml] = useState(campaign.bodyHtml ?? meta.defaultBody)
    const [config, setConfig] = useState<Record<string, unknown>>(campaign.config ?? {})
    const [saving, setSaving] = useState(false)

    // Template mode
    const [useTemplate, setUseTemplate] = useState(!!campaign.templateId)
    const [templateId, setTemplateId] = useState<string | null>(campaign.templateId ?? null)
    const [template, setTemplate] = useState<EmailTemplateT | null>(null)
    const [slotValues, setSlotValues] = useState<Record<string, unknown>>(
        (campaign.slotValues as Record<string, unknown>) ?? {},
    )
    const [templatePreview, setTemplatePreview] = useState('')
    const [contentPrompt, setContentPrompt] = useState('')
    const [aiBusy, setAiBusy] = useState(false)

    // Hydrate the template object whenever templateId is set but the full
    // object isn't loaded (e.g. on mount, after reloading the tab). Without
    // this, SlotValueForm + PreviewPane stay collapsed until the user re-picks.
    useEffect(() => {
        if (!templateId) {
            setTemplate(null)
            return
        }
        if (template?.id === templateId) return
        let cancelled = false
        ;(async () => {
            try {
                const r = await adminApi.getEmailTemplates({ category: 'CAMPAIGN' })
                if (cancelled) return
                const match = r.items.find(t => t.id === templateId)
                if (match) setTemplate(match)
            } catch {
                /* ignore — user can re-pick */
            }
        })()
        return () => {
            cancelled = true
        }
    }, [templateId, template?.id])

    useEffect(() => {
        if (!useTemplate || !templateId) return
        const t = setTimeout(async () => {
            try {
                const r = await adminApi.previewTemplate(templateId, slotValues)
                setTemplatePreview(r.html)
            } catch (e) {
                setTemplatePreview(
                    `<pre style="color:#C2410C;padding:32px;">${(e as Error).message}</pre>`,
                )
            }
        }, 300)
        return () => clearTimeout(t)
    }, [useTemplate, templateId, slotValues])

    async function aiFill() {
        if (!templateId || !contentPrompt.trim()) return
        setAiBusy(true)
        try {
            const r = await adminApi.aiGenerateContent(templateId, contentPrompt)
            setSlotValues(r.slotValues)
        } finally {
            setAiBusy(false)
        }
    }

    const sendHour = (config.sendHour as number | undefined) ?? 10
    const dayOffset =
        (config.dayOffset as number | undefined) ??
        (campaign.type === 'WELCOME'
            ? 0
            : campaign.type === 'DORMANT_7'
              ? 7
              : campaign.type === 'DORMANT_14'
                ? 14
                : 1)

    const handleSave = async () => {
        setSaving(true)
        try {
            await adminApi.updateCampaign(campaign.type, {
                enabled,
                subject,
                bodyHtml,
                config: { ...config, sendHour, dayOffset },
                templateId: useTemplate ? templateId : null,
                slotValues: useTemplate ? slotValues : null,
            })
            await onSaved()
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="space-y-6">
            <button
                onClick={onBack}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <ChevronLeft className="h-4 w-4" /> Back to campaigns
            </button>

            <SectionTitle
                title={meta.label}
                description={meta.description}
                actions={
                    <PillButton variant="primary" onClick={handleSave} disabled={saving}>
                        <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
                    </PillButton>
                }
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                    <Card>
                        <CardContent>
                            <Toggle
                                checked={enabled}
                                onChange={setEnabled}
                                label={enabled ? 'Campaign is on' : 'Campaign is off'}
                                description={
                                    enabled
                                        ? 'Recipients matching the trigger will receive this email.'
                                        : 'No sends will be queued.'
                                }
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={useTemplate}
                                    onChange={e => setUseTemplate(e.target.checked)}
                                />
                                Use a template
                            </label>
                            {useTemplate && (
                                <div className="space-y-4">
                                    <TemplatePicker
                                        category="CAMPAIGN"
                                        value={templateId}
                                        onChange={(id, t) => {
                                            setTemplateId(id)
                                            setTemplate(t)
                                            setSlotValues(
                                                (t?.previewValues as Record<string, unknown> | null) ?? {},
                                            )
                                        }}
                                    />
                                    {template && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-3">
                                                <Field label="Describe the content (for AI fill)">
                                                    <textarea
                                                        value={contentPrompt}
                                                        onChange={e => setContentPrompt(e.target.value)}
                                                        className="block h-24 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                                    />
                                                </Field>
                                                <SlotValueForm
                                                    schema={template.variables ?? []}
                                                    values={slotValues}
                                                    onChange={setSlotValues}
                                                    onAIFill={aiFill}
                                                    aiBusy={aiBusy}
                                                />
                                            </div>
                                            <PreviewPane
                                                html={templatePreview}
                                                heightClass="h-[500px]"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {!useTemplate && (
                        <>
                            <Card>
                                <CardContent>
                                    <Field
                                        label="Subject line"
                                        hint={
                                            'Use {{firstName}} or {{name}} for personalisation.'
                                        }
                                        required
                                    >
                                        <Input
                                            value={subject}
                                            onChange={e => setSubject(e.target.value)}
                                            placeholder={meta.defaultSubject}
                                        />
                                    </Field>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="!text-sm">Email body</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <RichTextEditor
                                        value={bodyHtml}
                                        onChange={setBodyHtml}
                                        variables={CAMPAIGN_VARIABLES}
                                        placeholder="Write the body of the campaign…"
                                        minHeight={300}
                                    />
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>

                <div className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Cadence</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Field
                                label={
                                    campaign.type === 'WELCOME'
                                        ? 'Send on day'
                                        : 'Days after threshold'
                                }
                            >
                                <Input
                                    type="number"
                                    value={dayOffset}
                                    onChange={e =>
                                        setConfig({
                                            ...config,
                                            dayOffset: Number(e.target.value),
                                        })
                                    }
                                />
                            </Field>
                            <Field label="Send at hour (0–23, user timezone)">
                                <Input
                                    type="number"
                                    min={0}
                                    max={23}
                                    value={sendHour}
                                    onChange={e =>
                                        setConfig({
                                            ...config,
                                            sendHour: Math.max(
                                                0,
                                                Math.min(23, Number(e.target.value))
                                            ),
                                        })
                                    }
                                />
                            </Field>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="!text-sm">Trigger condition</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <pre className="overflow-x-auto rounded bg-gray-50 p-3 font-mono text-xs text-gray-700">
                                {campaign.type === 'WELCOME'
                                    ? 'user.createdAt < now() AND not yet welcomed'
                                    : campaign.type === 'DORMANT_7'
                                      ? 'lastActiveAt < now() - 7d AND no D7 yet'
                                      : campaign.type === 'DORMANT_14'
                                        ? 'lastActiveAt < now() - 14d AND no D14 yet'
                                        : "feature_used != 'X' AND used_anything > 7d"}
                            </pre>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    )
}
